import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  CashierTradingAccount,
  CashierTransactionView,
} from "@/api/cashier";
import {
  AccountLabel,
  PaymentMethodLabel,
} from "@/components/transaction/transaction-history-table-filter/TransactionHistoryTableFilter";
import { useCurrencies } from "@/hooks/deposit/useCurrencies.ts";

type UseDynamicOptionsProps = {
  accounts: CashierTradingAccount[];
  transactions: CashierTransactionView[];
};

const ALL_OPTION_VALUE = "all";

export const useDynamicOptions = ({
  accounts,
  transactions,
}: UseDynamicOptionsProps) => {
  const { t } = useTranslation();
  const { currencies } = useCurrencies();

  const accountOptions = useMemo(() => {
    // Use a Set to collect unique account IDs efficiently
    const accountIdSet = new Set<string>();
    transactions.forEach((transaction) => {
      if (transaction.from_trading_account) {
        accountIdSet.add(transaction.from_trading_account);
      }
      if (transaction.to_trading_account) {
        accountIdSet.add(transaction.to_trading_account);
      }
    });

    const baseList = Array.from(accountIdSet).map((accountPNXId) => {
      const account = accounts.find((a) => a.id === accountPNXId);
      return {
        tradingPlatformName: account?.tradingPlatform,
        tradingPlatformId: account?.accountLoginId,
        value: accountPNXId,
      };
    });

    const listWithLabel = baseList.map((item) => ({
      value: item.value,
      label: (
        <AccountLabel
          tradingPlatform={item.tradingPlatformName || "Pepperstone"}
          accountId={item.tradingPlatformId || item.value}
        />
      ),
    }));

    return listWithLabel.length > 1
      ? [
          {
            label: t("transactions.history.filterOptions.all"),
            value: ALL_OPTION_VALUE,
          },
          ...listWithLabel,
        ]
      : listWithLabel;
  }, [transactions, accounts, t]);

  const paymentMethodOptions = useMemo(() => {
    const map = new Map<string, { name: string; logo_url: string }>();
    transactions.forEach((transaction) => {
      if (transaction.saved_payment_account) {
        map.set(transaction.saved_payment_account.name, {
          name: transaction.saved_payment_account.name,
          logo_url: transaction.saved_payment_account.logo_url,
        });
      }
    });

    const baseList = Array.from(map.values()).map((method) => ({
      logo: method.logo_url,
      value: method.name,
    }));

    const listWithLabel = baseList.map((item) => ({
      value: item.value,
      label: <PaymentMethodLabel logo={item.logo} name={item.value} />,
    }));

    // Always include "All" option for payment methods
    return [
      {
        label: t("transactions.history.filterOptions.all"),
        value: ALL_OPTION_VALUE,
      },
      ...listWithLabel,
    ];
  }, [transactions, t]);

  const currencyOptions = useMemo(() => {
    const allCurrencyCodes = [
      ...new Set(transactions.map((t) => t.money.code)),
    ];

    const options = allCurrencyCodes.map((id) => {
      const currency = currencies?.[id];
      return {
        label: currency?.code?.toUpperCase() || id,
        value: id,
      };
    });

    return options.length > 1
      ? [
          {
            label: t("transactions.history.filterOptions.all"),
            value: ALL_OPTION_VALUE,
          },
          ...options,
        ]
      : options;
  }, [currencies, transactions, t]);

  return {
    accountOptions,
    paymentMethodOptions,
    currencyOptions,
  };
};
